<template>
  <section id="pricing" class="py-8 md:py-20 px-4 sm:px-6 md:px-12 lg:px-24 bg-white">
    <div class="bg-[#FEFEFE] p-4 sm:p-6 md:p-10 rounded-lg drop-shadow-lg w-full sm:w-full md:w-4/5 lg:w-3/4 mx-auto">
      <h2 class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-[#39ab58] text-center mb-8 md:mb-12">
        {{ $t('pricing.title') }}
      </h2>

      <div class="container space-y-8 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-x-8">
        <!-- Standard Plan -->
        <div
          class="relative p-6 sm:p-8 border border-gray-200 rounded-2xl shadow-sm flex flex-col transition-all duration-300 hover:ring-1 ring-[#22c55e] mb-6 lg:mb-0">
          <div class="flex-1">
            <h3 class="text-2xl sm:text-2xl font-[800] text-right" v-if="$i18n.locale === 'ar'">
              {{ $t('pricing.standard.title') }}
            </h3>
            <h3 class="text-2xl sm:text-2xl font-[800]" v-else>
              {{ $t('pricing.standard.title') }}
            </h3>


            <p class="mt-4 text-right" v-if="$i18n.locale === 'ar'">{{ $t('pricing.benefits') }}</p>
            <p class="mt-4" v-else>{{ $t('pricing.benefits') }}</p>

            <ul role="list" class="mt-4 sm:mt-6 space-y-4 sm:space-y-6 text-right" v-if="$i18n.locale === 'ar'">
              <li v-for="(feature, index) in standardFeatures" :key="index"
                class="flex items-center justify-end flex-row-reverse">
                <span class="ml-2 text-sm sm:text-base">{{ $t(feature) }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </li>
            </ul>
            <ul role="list" class="mt-4 sm:mt-6 space-y-4 sm:space-y-6 text-right" v-else>
              <li v-for="(feature, index) in standardFeatures" :key="index"
                class="flex items-center justify-end flex-row-reverse">
                <span class="ml-2 text-sm sm:text-base">{{ $t(feature) }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </li>
            </ul>
          </div>
          <a class="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 mt-6 block w-full py-2 sm:py-3 px-4 sm:px-6 border border-transparent rounded-md text-center font-medium text-sm sm:text-base"
            href="https://api.whatsapp.com/send?phone=963987292450">
            {{ $t('pricing.orderNow') }}
          </a>
        </div>

        <!-- Premium Plan -->
        <div
          class="relative p-6 sm:p-8 border border-gray-200 rounded-2xl shadow-sm flex flex-col transition-all duration-300 hover:ring-1 ring-[#22c55e]">
          <div class="flex-1">
            <h3 class="text-xl sm:text-2xl font-[800] text-right" v-if="$i18n.locale === 'ar'">
              {{ $t('pricing.premium.title') }}
            </h3>
            <h3 class="text-2xl sm:text-2xl font-[800]" v-else>
              {{ $t('pricing.premium.title') }}
            </h3>

            <p
              class="absolute top-0 py-1 px-3 sm:py-1.5 sm:px-4 bg-emerald-500 text-white rounded-full text-xs sm:text-xs font-semibold uppercase tracking-wide transform -translate-y-1/2">
              {{ $t('pricing.mostPopular') }}
            </p>


            <p class="mt-4 text-right" v-if="$i18n.locale === 'ar'">{{ $t('pricing.benefits') }}</p>
            <p class="mt-4" v-else>{{ $t('pricing.benefits') }}</p>

            <ul role="list" class="mt-4 sm:mt-6 space-y-4 sm:space-y-6 text-right" v-if="$i18n.locale === 'ar'">
              <li v-for="(feature, index) in premiumFeatures" :key="index"
                class="flex items-center justify-end flex-row-reverse">
                <span class="ml-2 text-sm sm:text-base">{{ $t(feature) }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </li>
            </ul>
            <ul role="list" class="mt-4 sm:mt-6 space-y-4 sm:space-y-6 text-right" v-else>
              <li v-for="(feature, index) in premiumFeatures" :key="index"
                class="flex items-center justify-end flex-row-reverse">
                <span class="ml-2 text-sm sm:text-base">{{ $t(feature) }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </li>
            </ul>
          </div>
          <a class="bg-emerald-500 text-white hover:bg-emerald-600 mt-6 block w-full py-2 sm:py-3 px-4 sm:px-6 border border-transparent rounded-md text-center font-medium text-sm sm:text-base"
            href="https://api.whatsapp.com/send?phone=963987292450">{{ $t('pricing.orderNow') }}</a>
        </div>
      </div>
    </div>
  </section>

  <div class="hidden md:block self-stretch divider-gradient"></div>
</template>


<script>
export default {
  name: 'PricingSection',
  data() {
    return {
      standardFeatures: [
        'pricing.features.tracking',
        'pricing.features.emergency',
        'pricing.features.app',
        'pricing.features.fleet'
      ],
      premiumFeatures: [
        'pricing.features.tracking',
        'pricing.features.emergency',
        'pricing.features.app',
        'pricing.features.fleet',
        'pricing.features.data',
        'pricing.features.analysis',
        'pricing.features.obd2canbus'
      ]
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;900&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;900&display=swap');

[dir="ltr"] h2,
[dir="ltr"] h3,
[dir="ltr"] p,
[dir="ltr"] span,
[dir="ltr"] a {
  font-family: "Poppins", sans-serif;
}

[dir="rtl"] h2,
[dir="rtl"] h3,
[dir="rtl"] p,
[dir="rtl"] span,
[dir="rtl"] a {
  font-family: "Cairo", sans-serif;
}

.divider-gradient {
  height: 22px;
  background: linear-gradient(90deg,
      rgba(162, 205, 222, 0) 0%,
      rgba(162, 205, 222, 1) 20%,
      rgba(215, 234, 236, 1) 49%,
      rgba(195, 230, 205, 1) 75%,
      rgba(195, 230, 205, 0) 100%,
      rgba(214, 234, 238, 1) 753%);
  width: 70%;
  margin: 0 auto;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  border-radius: 10px;
  filter: blur(3px);
}

[dir="rtl"] .divider-gradient {
  transform: scaleX(-1);
}

@media (max-width: 768px) {
  .divider-gradient {
    display: block;
  }
}
</style>
